import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, GripVertical, Pencil, Plus, Tags, Trash2, X } from "lucide-react";
import {
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";

import OverlayShell from "../components/OverlayShell";
import QueryState from "../components/QueryState";
import { useConfirm } from "../lib/confirm";
import {
  createTag,
  deleteTag,
  fetchTags,
  normalizeTagName,
  renameTag,
  reorderTags,
  TAG_NAME_MAX,
  type Tag,
} from "../lib/media/tags";
import { queryKeys } from "../lib/queryKeys";
import { useToast } from "../lib/toast";

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** ドラッグ中の持ち上げた行。dy は掴んだ位置からの移動量 */
interface DragState {
  index: number;
  dy: number;
  /** 行の高さ。何行ぶん動いたかの換算に使う */
  height: number;
}

/** 離したときに収まる位置。行の高さの半分を超えて動いたら隣に入る */
function dropIndexOf(drag: DragState, count: number): number {
  return clamp(drag.index + Math.round(drag.dy / drag.height), 0, count - 1);
}

export default function TagManager({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState<DragState | null>(null);
  const dragStartY = useRef(0);

  const tags = useQuery({ queryKey: queryKeys.tags, queryFn: fetchTags });

  async function refreshAll() {
    await queryClient.invalidateQueries({ queryKey: queryKeys.tags });
    // タグ名は一覧の絞り込みにも出るため、そちらも取り直す
    await queryClient.invalidateQueries({ queryKey: queryKeys.timelines });
  }

  /** 同名タグの一意制約(Postgres 23505)。名前変更で既存名を選んだとき */
  function isDuplicateName(e: unknown): boolean {
    return typeof e === "object" && e !== null && (e as { code?: unknown }).code === "23505";
  }

  /** 成否を返す。失敗時は呼び出し側が入力内容を保持できるようにする */
  async function run(
    action: () => Promise<void>,
    messages: { done: string; failed: string; duplicate?: string },
  ): Promise<boolean> {
    setBusy(true);
    try {
      await action();
      await refreshAll();
      toast(messages.done);
      return true;
    } catch (e) {
      // 詳細はユーザーに出さない。原因はコンソールで追う
      console.error(messages.failed, e);
      const duplicate = messages.duplicate !== undefined && isDuplicateName(e);
      toast(duplicate ? (messages.duplicate ?? messages.failed) : messages.failed, "error");
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function add() {
    const name = normalizeTagName(draft);
    if (name === null) return;
    // createTag は同名なら既存を返して「成功」してしまう。追加済みはここで知らせる
    if (tags.data?.some((tag) => tag.name === name) === true) {
      toast(`タグ「${name}」は登録済みです。`, "error");
      return;
    }
    // createTag は同名があればそれを返すため、二重に作られることはない
    const ok = await run(
      async () => {
        await createTag(name);
      },
      { done: `タグ「${name}」を登録しました。`, failed: `タグ「${name}」の登録に失敗しました。` },
    );
    // 失敗したときは打った名前を残す(打ち直しさせない)
    if (ok) setDraft("");
  }

  async function save() {
    const target = editing;
    if (target === null) return;
    const name = normalizeTagName(target.name);
    if (name === null) return;
    const ok = await run(() => renameTag(target.id, name), {
      done: `タグ「${name}」に変更しました。`,
      failed: `タグ「${name}」の変更に失敗しました。`,
      duplicate: `タグ「${name}」は登録済みです。`,
    });
    // 失敗したときは編集途中の内容を残す
    if (ok) setEditing(null);
  }

  async function remove(id: string, name: string) {
    const ok = await confirm({
      message: `タグ「${name}」を削除します。\n※写真・動画は削除されません。`,
      confirmLabel: "削除",
      tone: "danger",
    });
    if (!ok) return;
    await run(() => deleteTag(id), {
      done: `タグ「${name}」を削除しました。`,
      failed: `タグ「${name}」の削除に失敗しました。`,
    });
  }

  /** 並びを保存する。並び順は一覧のチップにもそのまま効く */
  async function applyOrder(next: Tag[]) {
    // 先に画面へ反映する。保存の往復を待たせると、続けて動かすときに手が止まる
    queryClient.setQueryData(
      queryKeys.tags,
      next.map((tag, index) => ({ ...tag, sortOrder: index })),
    );
    setBusy(true);
    try {
      await reorderTags(next);
      toast("並び順を変更しました。");
    } catch (e) {
      console.error("並び順の保存に失敗", e);
      toast("並び順の変更に失敗しました。", "error");
    } finally {
      setBusy(false);
      // 成否どちらでもDBの並びに合わせ直す
      await queryClient.invalidateQueries({ queryKey: queryKeys.tags });
    }
  }

  function startDrag(event: ReactPointerEvent<HTMLButtonElement>, index: number) {
    if (busy || editing !== null) return;
    // 行の高さは掴んだ行から測る。行は全部同じ作りなので1つで足りる
    const row = event.currentTarget.closest("li");
    if (row === null) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStartY.current = event.clientY;
    setDrag({ index, dy: 0, height: row.offsetHeight });
  }

  function moveDrag(event: ReactPointerEvent<HTMLButtonElement>) {
    setDrag((current) =>
      current === null ? null : { ...current, dy: event.clientY - dragStartY.current },
    );
  }

  function endDrag() {
    if (drag === null) return;
    const list = tags.data ?? [];
    const to = dropIndexOf(drag, list.length);
    const from = drag.index;
    setDrag(null);
    if (to === from) return;

    const next = [...list];
    const [row] = next.splice(from, 1);
    if (row === undefined) return;
    next.splice(to, 0, row);
    void applyOrder(next);
  }

  function cancelDrag() {
    setDrag(null);
  }

  const count = tags.data?.length ?? 0;
  const dropIndex = drag === null ? null : dropIndexOf(drag, count);

  /** ドラッグ中の各行の見た目。持ち上げた行は指に追従し、通り過ぎた行は場所を空ける */
  function rowStyle(index: number): CSSProperties | undefined {
    if (drag === null || dropIndex === null) return undefined;
    if (index === drag.index) {
      return { transform: `translateY(${String(drag.dy)}px)` };
    }
    if (drag.index < index && index <= dropIndex) {
      return { transform: `translateY(-${String(drag.height)}px)`, transition: "transform 150ms" };
    }
    if (dropIndex <= index && index < drag.index) {
      return { transform: `translateY(${String(drag.height)}px)`, transition: "transform 150ms" };
    }
    return { transition: "transform 150ms" };
  }

  return (
    <OverlayShell title="タグ管理" icon={Tags} onClose={onClose}>
      <div className="flex w-full flex-col p-4">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void add();
          }}
          className="flex gap-2 pb-4"
        >
          <input
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
            placeholder="新しいタグ名"
            maxLength={TAG_NAME_MAX}
            autoComplete="off"
            data-1p-ignore
            className="flex-1 rounded-lg border border-[var(--color-line)] px-3 py-2 text-base"
          />
          <button
            type="submit"
            disabled={busy || normalizeTagName(draft) === null}
            className="flex items-center gap-1 rounded-lg bg-[var(--color-accent)] px-4 py-2 text-sm text-white transition enabled:hover:opacity-85 disabled:opacity-50"
          >
            <Plus className="size-4" aria-hidden />
            追加
          </button>
        </form>

        <QueryState
          isPending={tags.isPending}
          isError={tags.isError}
          errorMessage="タグの取得に失敗しました。"
          isEmpty={tags.isSuccess && tags.data.length === 0}
          emptyMessage="タグは登録されていません。"
          emptyClassName="p-10 text-center text-sm"
        />

        {/* 一覧だけを流し、入力欄は上に残す。高さは画面基準で確定させる
            (親から flex で渡すと確定せず、スクロール量が生まれないまま
            overscroll-contain がホイールの行き場を塞いでいた)。
            全画面のスマホ・タブレットは見出しと入力欄のぶんを引いた残り、
            PC のモーダルは画面の6割まで。dvh はアドレスバーの伸縮に追従する。
            overflow-y だけだと横が auto に昇格して横バーが出るので x は塞ぐ */}
        <ul className="flex max-h-[calc(100dvh-10rem)] flex-col overflow-x-hidden overflow-y-auto xl:max-h-[60vh]">
          {tags.data?.map((tag, index) => {
            const isEditing = editing?.id === tag.id;
            const lifted = drag !== null && drag.index === index;
            return (
              <li
                key={tag.id}
                style={rowStyle(index)}
                className={`flex items-center gap-2 border-b border-[var(--color-line)] bg-[var(--color-bg)] py-3 transition-colors hover:bg-[color-mix(in_srgb,var(--color-ink)_4%,transparent)] ${
                  lifted ? "relative z-10 shadow-md" : ""
                }`}
              >
                {isEditing ? (
                  <>
                    <input
                      autoFocus
                      value={editing.name}
                      onChange={(event) => {
                        setEditing({ id: tag.id, name: event.target.value });
                      }}
                      maxLength={TAG_NAME_MAX}
                      autoComplete="off"
                      data-1p-ignore
                      className="flex-1 rounded-lg border border-[var(--color-line)] px-3 py-1.5 text-base"
                    />
                    <button
                      type="button"
                      onClick={() => void save()}
                      // 空(空白だけ)のときも薄くする。押しても無反応より分かりやすい
                      disabled={busy || normalizeTagName(editing.name) === null}
                      aria-label="保存"
                      className="-m-1.5 flex size-8 items-center justify-center rounded-full transition-colors enabled:hover:bg-[color-mix(in_srgb,var(--color-ink)_7%,transparent)] text-[var(--color-teal)] disabled:opacity-50"
                    >
                      <Check className="size-5" aria-hidden />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditing(null);
                      }}
                      aria-label="やめる"
                      className="-m-1.5 flex size-8 items-center justify-center rounded-full transition-colors enabled:hover:bg-[color-mix(in_srgb,var(--color-ink)_7%,transparent)] text-[var(--color-sub)]"
                    >
                      <X className="size-5" aria-hidden />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onPointerDown={(event) => {
                        startDrag(event, index);
                      }}
                      onPointerMove={moveDrag}
                      onPointerUp={endDrag}
                      onPointerCancel={cancelDrag}
                      aria-label={`${tag.name} をドラッグして並べ替え`}
                      // touch-action を切らないと、ドラッグがページのスクロールに化ける
                      className={`touch-none text-[var(--color-sub)] ${
                        lifted ? "cursor-grabbing" : "cursor-grab"
                      }`}
                    >
                      <GripVertical className="size-5" aria-hidden />
                    </button>
                    <span className="flex-1 text-sm">{tag.name}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setEditing({ id: tag.id, name: tag.name });
                      }}
                      aria-label={`${tag.name} の名前を変える`}
                      className="-m-1.5 flex size-8 items-center justify-center rounded-full transition-colors enabled:hover:bg-[color-mix(in_srgb,var(--color-ink)_7%,transparent)] text-[var(--color-sub)]"
                    >
                      <Pencil className="size-4" aria-hidden />
                    </button>
                    <button
                      type="button"
                      onClick={() => void remove(tag.id, tag.name)}
                      disabled={busy}
                      aria-label={`${tag.name} を消す`}
                      className="-m-1.5 flex size-8 items-center justify-center rounded-full transition-colors enabled:hover:bg-[color-mix(in_srgb,var(--color-ink)_7%,transparent)] text-[var(--color-plum)] disabled:opacity-50"
                    >
                      <Trash2 className="size-4" aria-hidden />
                    </button>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </OverlayShell>
  );
}
